import { v4 as uuid } from 'uuid';
import { replaceCitations } from '@Utils/String';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import ResultsPreviewStorage from '@Services/AppDBStorage/Storages/ResultsPreviewStorage';
import HistoryHelper from '@Services/HistoryHelper';
import SearchStorage from '@Services/SearchStorage';
import get from 'lodash/get';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import assignIn from 'lodash/assignIn';
import isUndefined from 'lodash/isUndefined';
import cloneDeep from 'lodash/cloneDeep';
import { citations } from '@Constants';

const retrieveBreadcrumbs = (databaseId) => {
  const propertyPath = `history.breadcrumbs.${databaseId}`;
  return get(AuthUtils.getUser(), propertyPath, []) || [];
};

const retrieveActiveBreadcrumbs = () => {
  const { breadcrumbs } = AuthUtils.getUser().history;

  const dbIds = Object.keys(breadcrumbs);
  const activeBreadcrumbs = [];

  forEach(dbIds, (databaseId) => {
    const breadcrumbsPerDb = breadcrumbs[databaseId];
    const activeBreadcrumb = find(breadcrumbsPerDb, { active: true });

    activeBreadcrumbs.push(activeBreadcrumb);
  });

  return activeBreadcrumbs;
};

const retrieveRootBreadcrumb = (databaseId) => {
  const breadcrumbsPerDb = retrieveBreadcrumbs(databaseId);
  return breadcrumbsPerDb ? breadcrumbsPerDb[0] : null;
};

const retrieveRootActiveBreadcrumb = (activeBreadcrumb) => {
  if (!activeBreadcrumb) return null;
  const { databaseId, groupId } = activeBreadcrumb;
  const breadcrumbsPerDb = retrieveBreadcrumbs(databaseId);

  if (isUndefined(groupId)) {
    return breadcrumbsPerDb && breadcrumbsPerDb.length > 0 ? breadcrumbsPerDb[0] : null;
  }
  return breadcrumbsPerDb?.find((crumb) => crumb.groupId === groupId) || null;
};

const getActiveBreadcrumbForDb = (databaseId) => {
  const breadcrumbsPerDb = AuthUtils.getUser().history.breadcrumbs[databaseId];
  return find(breadcrumbsPerDb, { active: true });
};

const getBreadcrumbForDbByResultName = (databaseId, resultName) => {
  const breadcrumbsPerDb = AuthUtils.getUser().history.breadcrumbs[databaseId];
  return find(breadcrumbsPerDb, ['resultName', resultName]);
};

const removeGhosts = (breadcrumbs) => {
  forEach(breadcrumbs, (crumb, index) => {
    if (get(crumb, 'ghost', false)) {
      breadcrumbs.splice(index, breadcrumbs.length);
    }
  });
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const updateBreadcrumbs = (stateParams, revalidateGhost) => {
  if (!stateParams) return undefined;

  const currentUser = AuthUtils.getUser();

  const allBreadcrumbs = currentUser.history.breadcrumbs;
  const { databaseId } = stateParams;

  if (!allBreadcrumbs[databaseId]) {
    allBreadcrumbs[databaseId] = [];
  }

  // Each db should have separate chain of breadcrumbs
  const breadcrumbs = allBreadcrumbs[databaseId];

  let foundBreadcrumb = find(breadcrumbs, { resultName: stateParams.resultName });

  if (!foundBreadcrumb && stateParams.isHeatmapShown) {
    foundBreadcrumb = find(breadcrumbs, {
      heatmapResultName: stateParams.resultName,
      context: stateParams.fromContext,
    });
  }

  // when we do have a resultName we know the breadcrumbs already exist and
  // update ghost and active flags
  if (!isUndefined(foundBreadcrumb)) {
    // iterate over the crumbs and set the ghost and active properties
    forEach(breadcrumbs, (crumb) => {
      // eslint-disable-next-line no-param-reassign
      crumb.ghost = crumb.creationTime > foundBreadcrumb.creationTime && crumb.groupId === foundBreadcrumb.groupId;
      // eslint-disable-next-line no-param-reassign
      crumb.active = crumb.resultName === foundBreadcrumb.resultName;
    });

    if (revalidateGhost) {
      removeGhosts(breadcrumbs);
    }

    if (stateParams.sort) {
      foundBreadcrumb.sort = stateParams.sort;
    }
    if (stateParams.context) {
      foundBreadcrumb.context = stateParams.context;
    }
    if (stateParams.order) {
      foundBreadcrumb.order = stateParams.order;
    }
    if (!isUndefined(stateParams.isHeatmapShown)) {
      foundBreadcrumb.isHeatmapShown = stateParams.isHeatmapShown;
    }
    if (stateParams.fromContext) {
      foundBreadcrumb.fromContext = stateParams.fromContext;
    }
    if (stateParams.hitSetKey) {
      foundBreadcrumb.hitSetKey = stateParams.hitSetKey;
    }
    if (stateParams.heatmapResultName && !foundBreadcrumb.heatmapResultName) {
      foundBreadcrumb.heatmapResultName = stateParams.heatmapResultName;
    }
    if (stateParams.ascending) {
      foundBreadcrumb.ascending = stateParams.ascending;
    }
    if (stateParams.sortedMatrixName) {
      foundBreadcrumb.sortedMatrixName = stateParams.sortedMatrixName;
    }
    if (stateParams.index) {
      foundBreadcrumb.index = stateParams.index;
    }
    if (stateParams.isRow) {
      foundBreadcrumb.isRow = stateParams.isRow;
    }
    if (stateParams.criteria) {
      foundBreadcrumb.criteria = stateParams.criteria;
    }
    if (stateParams.dataFormCriteria) {
      foundBreadcrumb.dataFormCriteria = stateParams.dataFormCriteria;
    }
    if (stateParams.pageNumber) {
      foundBreadcrumb.pageNumber = stateParams.pageNumber;
    }
    if (!isUndefined(stateParams.substanceGridView)) {
      foundBreadcrumb.substanceGridView = stateParams.substanceGridView;
    }
    if (!isUndefined(stateParams.resultsState)) {
      foundBreadcrumb.resultsState = stateParams.resultsState;
    }
    if (!isUndefined(stateParams.expandPatentFamilyGroup)) {
      foundBreadcrumb.expandPatentFamilyGroup = stateParams.expandPatentFamilyGroup;
    }
    if (!isUndefined(stateParams.filters)) {
      foundBreadcrumb.filters = stateParams.filters;
    }

    AuthUtils.updateUser({ history: currentUser.history });

    return foundBreadcrumb;
  }
  return null;
};

const updateActiveParams = (resultName, databaseId) => {
  const currentUser = AuthUtils.getUser();

  if (!isUndefined(resultName)) {
    currentUser.history.viewedResultsParams.resultName = resultName;
  }

  if (!isUndefined(databaseId)) {
    currentUser.history.viewedResultsParams.databaseId = databaseId;
  }

  AuthUtils.updateUser({ history: currentUser.history });
};

const activateBreadcrumb = (resultName, databaseId) => {
  const stateParams = {
    resultName,
    databaseId,
  };

  updateActiveParams(resultName, databaseId);
  updateBreadcrumbs(stateParams);

  return getActiveBreadcrumbForDb(databaseId);
};

/**
 * @description decides what should be inherited form the previous crumb
 * */
const BASE_INHERITED_PROPS = ['substanceGridView', 'hideSummary'];

const inheritFromPrevious = (currentCrumb, newCrumb) => {
  const shouldInheritSemanticSearchProps = currentCrumb?.semanticSearchIncluded && newCrumb?.context === citations;

  const inheritedProps = shouldInheritSemanticSearchProps
    ? [...BASE_INHERITED_PROPS, 'semanticSearchIncluded', 'semanticResultName', 'textQuery']
    : BASE_INHERITED_PROPS;

  inheritedProps.forEach((prop) => {
    if (isUndefined(newCrumb[prop]) && !isUndefined(currentCrumb[prop])) {
      // eslint-disable-next-line no-param-reassign
      newCrumb[prop] = currentCrumb[prop];
    }
  });
};

const resolveNewBreadcrumbAssistantEligibility = (currentCrumb, newCrumb, useDestinationAssistantEligibility) => {
  if (useDestinationAssistantEligibility) {
    // A context switch is a new eligibility decision from the cohitset response. Treat an
    // omitted value as ineligible rather than falling back to the previous results context.
    // eslint-disable-next-line no-param-reassign
    newCrumb.assistantEligible = newCrumb.assistantEligible === true;
    return;
  }

  // A root breadcrumb has no previous crumb in its group, so its own backend value remains
  // authoritative. Every other result set in the same chain inherits the current effective
  // value; filter/limit/reference endpoints must not replace it with their own response flag.
  if (!currentCrumb || currentCrumb.groupId !== newCrumb.groupId) return;

  const rootBreadcrumb = retrieveRootActiveBreadcrumb(currentCrumb);
  const inheritedAssistantEligible = currentCrumb.assistantEligible ?? rootBreadcrumb?.assistantEligible;

  if (isUndefined(inheritedAssistantEligible)) {
    // eslint-disable-next-line no-param-reassign
    delete newCrumb.assistantEligible;
  } else {
    // eslint-disable-next-line no-param-reassign
    newCrumb.assistantEligible = inheritedAssistantEligible;
  }
};

const formatNewHistory = async (newCrumb, currentUser, currentCrumb) => {
  try {
    // set history previousResult from previous breadcrumb
    if (currentCrumb) {
      // eslint-disable-next-line no-param-reassign
      newCrumb.previousResult = {
        context: currentCrumb.context,
        resultSize: currentCrumb.resultSize || currentCrumb.totalResult,
        query: currentCrumb?.quickSearchItem?.content || currentCrumb?.textQuery,
      };
    }

    const crumb = await HistoryHelper.formatNewHistory(newCrumb);
    // eslint-disable-next-line no-param-reassign
    delete newCrumb.whereClauseImportToken;
    Object.assign(newCrumb, crumb);

    const { breadcrumbs } = currentUser.history;
    breadcrumbs[crumb.databaseId] = breadcrumbs[crumb.databaseId] || [];
    breadcrumbs[crumb.databaseId].push(newCrumb);

    await AuthUtils.updateUser({ history: currentUser.history });
    return newCrumb;
  } catch (error) {
    return Logger.reject(error);
  }
};

const getNewBreadcrumb = (params, resultSize, filters, groupId) => {
  const newCrumb = cloneDeep(params);

  return assignIn(newCrumb, {
    totalResult: resultSize,
    creationTime: Date.now(),
    filters,
    active: true,
    ghost: false,
    resultsState: {
      checkedItems: [],
      partialCheckedItems: [],
    },
    groupId,
    id: uuid(),
  });
};

const addAdvancedSearchDescription = (crumb) =>
  SearchStorage.get(crumb.searchId)
    .then((advancedSearchParams) => {
      if (advancedSearchParams.queryFactItem) {
        // eslint-disable-next-line no-param-reassign
        crumb.description = `${replaceCitations(crumb.context)}: `;
        HistoryHelper.getFormattedFactItems(advancedSearchParams.queryFactItem, crumb);
      }

      return crumb;
    })
    .catch(Logger.reject);

const addWhereClauseImportToken = (crumb) =>
  ResultsPreviewStorage.get(crumb.searchId)
    .then(({ allResults } = {}) => {
      // Try to retrieve "whereClauseImportToken". That param need for Edit Query
      const result = find(allResults || [], { hitSetKey: crumb.hitSetKey });
      if (result) {
        // eslint-disable-next-line no-param-reassign
        crumb.whereClauseImportToken = result.whereClauseImportToken;
      }

      return crumb;
    })
    .catch(Logger.reject);

const resetBreadcrumbs = () => {
  const currentUser = AuthUtils.getUser();

  currentUser.history.breadcrumbs = {};

  AuthUtils.updateUser({ history: currentUser.history });
};

const resetBreadcrumbsForDB = (databaseId) => {
  const currentUser = AuthUtils.getUser();

  currentUser.history.breadcrumbs[databaseId] = [];

  AuthUtils.updateUser({ history: currentUser.history });
};

const getResultsStateForDb = (dbId, key) => {
  const crumb = getActiveBreadcrumbForDb(dbId);
  return get(crumb, `resultsState.${key}`);
};

const updateResultsStateForDb = (dbId, key, value) => {
  const crumb = getActiveBreadcrumbForDb(dbId);
  // No active breadcrumb yet can happen briefly during navigation from History
  // (activateBreadcrumb runs before createBreadcrumb resolves) or when a session-only
  // recent-history entry has not yet been materialised into a crumb.
  if (!crumb) return undefined;
  assignIn(crumb.resultsState || {}, {
    [key]: value,
  });
  return updateBreadcrumbs(crumb);
};

const buildNewCrumbPromise = async (newCrumb, currentUser, currentActive) => {
  try {
    if (newCrumb.isFromAdvancedSearch) {
      const data = await addAdvancedSearchDescription(newCrumb);
      return formatNewHistory(data, currentUser, currentActive);
    }
    if (!newCrumb.whereClauseImportToken) {
      const data = await addWhereClauseImportToken(newCrumb);
      return formatNewHistory(data, currentUser, currentActive);
    }
    return formatNewHistory(newCrumb, currentUser, currentActive);
  } catch (error) {
    return Logger.reject(error);
  }
};

/**
 * @param {Boolean} [noStore] indicates whether new crumb should be stored as recent search or not
 * @param {String} groupId
 * @description generic way to keep track of the history AND breadcrumbs
 * */
const createBreadcrumb = async (stateParams, resultSize, filters, noStore, groupId, options = {}) => {
  const { useDestinationAssistantEligibility = false } = options;
  const currentUser = AuthUtils.getUser();
  const currentDbBreadcrumbs = currentUser.history.breadcrumbs[stateParams.databaseId];
  const existingBreadcrumb = find(currentDbBreadcrumbs, { resultName: stateParams.resultName });

  // but prevent duplication of crumbs because of not yet loaded result name
  if ((!existingBreadcrumb || stateParams.isRerun) && stateParams.resultName) {
    const currentActive = find(currentDbBreadcrumbs, { active: true });
    const targetGroup = groupId || get(currentActive, 'groupId');

    const newCrumb = getNewBreadcrumb(stateParams, resultSize, filters, targetGroup);
    resolveNewBreadcrumbAssistantEligibility(currentActive, newCrumb, useDestinationAssistantEligibility);
    // Inherit some properties from the previous active breadcrumb
    if (currentActive) {
      inheritFromPrevious(currentActive, newCrumb);
      currentActive.active = false;
    }

    removeGhosts(currentDbBreadcrumbs);

    const result = await buildNewCrumbPromise(newCrumb, currentUser, currentActive);

    if (!noStore) {
      try {
        await HistoryHelper.storeRecentHistory(newCrumb);
      } catch (error) {
        Logger.error(error);
      }
    }

    return result;
  }
  return Promise.resolve(updateBreadcrumbs(stateParams));
};

const createContextSwitchBreadcrumb = (stateParams, resultSize) =>
  createBreadcrumb(stateParams, resultSize, undefined, undefined, undefined, {
    useDestinationAssistantEligibility: true,
  });

export default {
  createBreadcrumb,
  createContextSwitchBreadcrumb,
  resetBreadcrumbs,
  retrieveBreadcrumbs,
  retrieveRootBreadcrumb,
  retrieveRootActiveBreadcrumb,
  retrieveActiveBreadcrumbs,
  getActiveBreadcrumbForDb,
  getBreadcrumbForDbByResultName,
  activateBreadcrumb,
  updateActiveParams,
  updateBreadcrumbs,
  resetBreadcrumbsForDB,
  getResultsStateForDb,
  updateResultsStateForDb,
};
